import { FormikField } from 'components/common/form/field/FormikField';
import { CollapsibleSection } from 'components/CollapsibleSection';
import { getCampaignLimitationAddonKeys } from 'core/agency/CampaignLimitationAddonKeys';
import {
  CHANNEL_GROUP_ID,
  ChannelGroupSetting
} from 'core/channelGroup/ChannelGroupSettings';
import { connect } from 'formik';
import i18next from 'i18n';
import _ from 'lodash';
import { FC } from 'react';
import { getChannelGroupName, isChannelEnabled } from './agencyAddonVisibility';
import { AddonSectionPlaceholder } from './AddonSectionPlaceholder';
import styles from './agencyForm.module.scss';

/**
 * 每個 campaign limitation 開關的 label i18n key override。
 * 沒列在這裡的 key 一律走 `agency.addonItem.campaignLimitation.${key}`；
 * 列在這裡的（gogan 的 target_audience、outdoor 專屬開關）改指既有 i18n key，
 * 避免重複新增翻譯。
 */
const CAMPAIGN_LIMITATION_LABEL_I18N_KEY: Record<string, string> = {
  target_audience: 'agency.gogan.addonItem.campaignLimitation.target_audience',
  // outdoor 專屬開關沿用既有 i18n key（避免重複新增翻譯）
  storeNum: 'productGroup.space.attributes.titles.storeNum',
  cityGroup: 'productGroup.space.attributes.titles.cityGroup',
  administrativeArea: 'productGroup.space.attributes.titles.administrativeArea',
  outdoorPlaceCategories:
    'productGroup.space.attributes.titles.outdoorPlaceCategories',
  deviceCharacteristic:
    'productGroup.space.attributes.titles.deviceCharacteristic',
  systemRecommendOutdoorDevice: 'limitation.labels.systemRecommendOutdoorDevice'
};

const getCampaignLimitationLabelI18nKey = (key: string): string =>
  CAMPAIGN_LIMITATION_LABEL_I18N_KEY[key] ??
  `agency.addonItem.campaignLimitation.${key}`;

/**
 * 要渲染 campaign limitation section 的 channel group 清單：
 *   RTB + 其餘 outdoor channel groups（去重，依 config.id），
 *   且只保留 channel add-on 有開、且渠道本身未停用的 channel group
 *   （`isChannelEnabled`，與其他 add-on 分頁同一個收斂點）。
 * RTB 以靜態實例保證候選，outdoor groups 取 registry 現況；
 * 最終是否顯示由 channel add-on 開關決定。
 */
const getCampaignLimitationChannelGroups = (
  addonFeatures: any
): ChannelGroupSetting[] => {
  const channelGroups = [
    ChannelGroupSetting.RTB,
    ...ChannelGroupSetting.getOutdoorChannelSettings()
  ];
  const seen = new Set<number>();
  return channelGroups.filter((channelGroup) => {
    const id = channelGroup.config.id;
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return isChannelEnabled(addonFeatures, channelGroup);
  });
};

const AgencyCampaignTAAddonForm: FC<any> = (props) => {
  const { addonFeatures } = props.formik.values;

  const renderCampaignLimitationSections = () =>
    getCampaignLimitationChannelGroups(addonFeatures).map((channelGroup) => {
      const channelGroupId = channelGroup.config.id;
      const keys = getCampaignLimitationAddonKeys(channelGroup);
      const isRtb = channelGroupId === CHANNEL_GROUP_ID.RTB;
      return (
        <CollapsibleSection
          key={channelGroupId}
          title={getChannelGroupName(channelGroup)}
          collapsible={true}
        >
          <div className={styles.fieldContent}>
            {keys.map((key) => {
              const name = `addonFeatures.campaignLimitation.channel_${channelGroupId}_${key}`;
              // RTB 的 city 開關依賴同 group 的 country
              if (isRtb && key === 'city') {
                return (
                  <FormikField.Switch
                    key={key}
                    label={i18next.t(getCampaignLimitationLabelI18nKey(key))}
                    name={name}
                    disabled={
                      !_.get(
                        addonFeatures,
                        `campaignLimitation.channel_${channelGroupId}_country`
                      )
                    }
                    hint={i18next.t('agency.addonItem.dependenceHint', {
                      addon: `${i18next.t('agency.addonGroup.campaignLimitation')} > ${i18next.t('agency.addonItem.campaignLimitation.country')}`
                    })}
                  />
                );
              }
              return (
                <FormikField.Switch
                  key={key}
                  label={i18next.t(getCampaignLimitationLabelI18nKey(key))}
                  name={name}
                />
              );
            })}
          </div>
        </CollapsibleSection>
      );
    });

  const sections = renderCampaignLimitationSections();

  if (sections.length === 0) {
    return (
      <div className={styles.agencyFrom}>
        <AddonSectionPlaceholder
          message={i18next.t('agency.placeholders.addon', {
            parent: i18next.t('agency.addonGroup.channel'),
            child: i18next.t('agency.addonGroup.campaignLimitation')
          })}
        />
      </div>
    );
  }

  return (
    <div className={styles.agencyFrom}>
      <fieldset>
        <legend>
          <span>{i18next.t('agency.addonGroup.campaignLimitation')}</span>
        </legend>
        <div className={styles.sectionsShadow}>{sections}</div>
      </fieldset>
    </div>
  );
};

export default connect(AgencyCampaignTAAddonForm);
